import { useCallback, useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import { AssetType, assetTypes } from "./types"

const paramKey = "types"

// useAssetTypesFilter reads the current asset-type filter as a set from the URL
// query string. An absent (or empty) param means every asset type is included;
// callers fan out over `types`, which is the array form the fanout expects.
export function useAssetTypesFilter(): {
	selected: Set<AssetType>
	setSelected(next: Set<AssetType>): void
	types: AssetType[]
} {
	const [params, setParams] = useSearchParams()
	const raw = params.get(paramKey) ?? ""
	const { selected, types } = useMemo(() => {
		const parsed = raw
			.split(",")
			.map((s) => s.trim())
			.filter((s): s is AssetType => (assetTypes as readonly string[]).includes(s))
		const set = new Set<AssetType>(parsed)
		return {
			selected: set,
			types: set.size === 0 ? ([...assetTypes] as AssetType[]) : assetTypes.filter((t) => set.has(t)),
		}
	}, [raw])
	const setSelected = useCallback(
		(next: Set<AssetType>): void => {
			const nextParams = new URLSearchParams(params)
			const preserved = assetTypes.filter((t) => next.has(t))
			if (preserved.length === 0 || preserved.length === assetTypes.length) nextParams.delete(paramKey)
			else nextParams.set(paramKey, preserved.join(","))
			setParams(nextParams)
		},
		[params, setParams],
	)
	return { selected, setSelected, types }
}
